import {
  IExtendedFile,
  IFile,
  IMusic,
  IUserProfile,
} from "../../../common/commonTypes";
import { getSrcFromOptionalFile, isVideo } from "../../../helpers/fileHelper";
import { MusicFormValues } from "./UploadMusic";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { validateSingleFile } from "./UploadTrackSchema";
import { MimeEnum } from "../../../common/commonEnums";
import { useSession } from "../../../queries/authentication.query";
import { useUploadMusicContext } from "./useUploadMusic";

const getSrcIfValid = async (field: string, extendedFile?: IExtendedFile) => {
  if (!extendedFile) {
    return undefined;
  }

  if (!(await validateSingleFile(field, extendedFile))) {
    return undefined;
  }

  return getSrcFromOptionalFile(extendedFile.file);
};

export const useMusicPreview = (): IMusic => {
  const { values } = useFormikContext<MusicFormValues>();
  const { user } = useSession();
  const [coverPreview, setCoverPreview] = useState<Partial<IFile>>();
  const [audioPreview, setAudioPreview] = useState<Partial<IFile>>();
  const [posterPreview, setPosterPreview] = useState<Partial<IFile>>();
  const musicContext = useUploadMusicContext();

  useEffect(() => {
    const updateSrc = async () => {
      const mime = values.cover?.file?.type as MimeEnum;
      // use the uploaded url for videos to fetch the transformed version of the file
      if (mime && isVideo(mime)) {
        const src = musicContext.cover?.url;
        setCoverPreview({
          url: src,
          mime: mime,
        });
      } else {
        // use base64 url from local file for images
        const src = await getSrcIfValid("cover", values.cover);
        setCoverPreview({
          url: src,
          mime: mime,
        });
      }
    };
    updateSrc();
  }, [values.cover, musicContext.cover]);

  useEffect(() => {
    const updateSrc = async () => {
      const src = await getSrcIfValid("masterMp3", values.masterMp3);
      setAudioPreview({
        url: src,
        mime: values.masterMp3?.file?.type as MimeEnum,
      });
    };
    updateSrc();
  }, [values.masterMp3]);

  useEffect(() => {
    const updateSrc = async () => {
      const src = await getSrcIfValid("poster", values.poster);
      setPosterPreview({ url: src });
    };
    updateSrc();
  }, [values.poster]);

  return {
    artist: user as IUserProfile,
    title: values.name,
    description: values.description,
    genres: values.genres || [],
    countries: values.countries || [],
    master_length: values.masterWav?.meta?.duration,
    languages: values.languages || [],
    cover: coverPreview,
    full: audioPreview,
    poster: posterPreview,
  } as IMusic;
};
